import { CreateNewGroupIcon, InviteNewIcon } from '@/assets/images';
import { AppIcon, Checkbox, ListTile, ListTileSkeleton, SearchBox, SmartListView } from '@/components';
import { useChatFeature } from '@/features/chats';
import { ExternalBadge } from '@/features/invite/ui';
import { useLanguage } from '@/localization/useLanguage';
import PropTypes from 'prop-types';
import React, { memo, useCallback, useEffect } from 'react';
import { UserMail, Username, UserProfile } from '../../ui';
import { useUsersListViewModel } from '../viewmodel/useUsersListViewModel';
import './usersList.scss';

/**
 * Individual user item component.
 * Pure rendering — all actions come from props.
 */
const UserItem = memo(({ user, isSelectable, isSelected, onToggle, onOpenUser }) => {
   const { userId } = user;

   const handleItemClick = () => {
      if (isSelectable) {
         onToggle?.(userId);
         return;
      }
      onOpenUser?.(userId);
   };

   return (
      <ListTile
         avatar={<UserProfile userId={userId} className="chat-avatar chat-avatar-fallback ct-flexCenter" />}
         title={<Username userId={userId} />}
         subTitle={<UserMail userId={userId} className="user-user-status ct-ellipsis" />}
         trailing={
            <div className="ct-flex ct-aiCenter ct-gap8">
               <ExternalBadge isExternalUser={user?.isExternal} className="external-badge--compact" />
               {isSelectable && (
                  <div className="user-selection-section">
                     <Checkbox checked={isSelected} onChange={() => onToggle?.(userId)} />
                  </div>
               )}
            </div>
         }
         onClick={handleItemClick}
         className={`user-item-container ${isSelected ? 'selected' : ''}`}
      />
   );
});

UserItem.displayName = 'UserItem';

UserItem.propTypes = {
   user: PropTypes.shape({
      userId: PropTypes.string.isRequired,
      status: PropTypes.string,
      mobileNumber: PropTypes.string,
      isExternal: PropTypes.bool,
   }).isRequired,
   isSelectable: PropTypes.bool,
   isSelected: PropTypes.bool,
   onToggle: PropTypes.func,
   onOpenUser: PropTypes.func,
};

const ListEmptyComponent = ({ searchText }) => {
   const { t } = useLanguage();
   return (
      <div className="empty-state ct-flex ct-flexCenter ct-fcol">
         <p>{searchText ? t('common.NO_USERS_FOUND') : t('common.NO_USERS_AVAILABLE')}</p>
      </div>
   );
};

ListEmptyComponent.propTypes = {
   searchText: PropTypes.string,
};

/**
 * Pure rendering component — zero business logic.
 * All actions and derived state come from useUsersListViewModel().
 */
export const UsersList = React.memo(({ config = {}, setGroupData }) => {
   const { t } = useLanguage();

   const {
      filteredUsers,
      loading,
      loadMore,
      hasMore,
      searchText,
      isSelectable,
      currentSelected,
      handleSelectionToggle,
      handleSearchChange,
      handleOpenUser,
      handleNavigateNewGroup,
      handleOpenInviteFlow,
   } = useUsersListViewModel(config);
   const inviteNewMemberEnabled = useChatFeature('invite_new_member');

   useEffect(() => {
      setGroupData?.({});
   }, []);

   const renderItem = useCallback(
      ({ item, index }) => {
         const isSelected = currentSelected.includes(item.userId);
         return (
            <UserItem
               user={item}
               index={index}
               isSelectable={isSelectable}
               isSelected={isSelected}
               onToggle={handleSelectionToggle}
               onOpenUser={handleOpenUser}
            />
         );
      },
      [isSelectable, currentSelected, handleSelectionToggle, handleOpenUser],
   );

   const renderLoaderView = useCallback(
      (count = 1) =>
         Array.from({ length: count }, (_, i) => `skeleton-fixed-item-${i}`).map(key => <ListTileSkeleton key={key} />),
      [],
   );

   const keyExtractor = useCallback((item, index) => item?.userId + index, []);

   let skeletonCount = 1;
   if (filteredUsers.length === 0) {
      skeletonCount = 15;
   }
   const loadingBottomComponent = loading ? renderLoaderView(skeletonCount) : null;

   return (
      <div className="users-list-container ct-flex ct-fcol ct-flex1 ">
         <div className="search-box-wrapper">
            <SearchBox placeholder={t('common.SEARCH')} onChange={handleSearchChange} autoFocus={true} />
         </div>

         {config?.customHeader}

         {!config?.hideActions && (
            <div className="recent-chat-actions ct-flex ct-fcol ct-gap8">
               <button className="action-item ct-flex ct-aiCenter ct-gap12 ct-cursor" onClick={handleNavigateNewGroup}>
                  <div className="action-icon ct-flexCenter">
                     <AppIcon size="lg" icon={<CreateNewGroupIcon />} />
                  </div>
                  <span className="action-label">{t('common.CREATE_GROUP')}</span>
               </button>
               {inviteNewMemberEnabled && (
                  <button className="action-item ct-flex ct-aiCenter ct-gap12 ct-cursor" onClick={handleOpenInviteFlow}>
                     <div className="action-icon ct-flexCenter">
                        <AppIcon size="xl" icon={<InviteNewIcon />} />
                     </div>
                     <span className="action-label">{t('common.INVITE_NEW_MEMBER')}</span>
                  </button>
               )}
            </div>
         )}
         {!config?.hideActions && (
            <div className="recent-chat-contact-heading">
               <p>{t('common.CONTACT_ON_MIRRORFLY_TEAMS')}</p>
            </div>
         )}
         <div className="ct-flex ct-flex1 smart-list-view-list ct-scroll">
            <SmartListView
               id="users-list-virtualized"
               data={filteredUsers}
               renderItem={renderItem}
               keyExtractor={keyExtractor}
               itemHeight={72}
               onLoadBottom={loadMore}
               hasMoreBottom={hasMore}
               LoadingBottomComponent={loadingBottomComponent}
               ListEmptyComponent={loading ? null : <ListEmptyComponent searchText={searchText} />}
               ListFooterComponent={config?.customFooter}
            />
         </div>
      </div>
   );
});

UsersList.propTypes = {
   config: PropTypes.shape({
      selectable: PropTypes.bool,
      selectedUsers: PropTypes.arrayOf(PropTypes.string),
      onSelectionChange: PropTypes.func,
   }),
   setGroupData: PropTypes.func,
};
